/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Grid,
  Alert,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import { toast } from 'react-hot-toast';
import { getUserDisplayName } from '../../utils/userUtils';

const getInvoiceNumber = (invoiceId) => {
  if (!invoiceId) return 'N/A';
  const parts = invoiceId.split('_INV');
  if (parts.length < 2) return 'N/A';
  const num = parseInt(parts[1], 10);
  return isNaN(num) ? 'N/A' : num;
};

const EditPayment = ({ open, onClose, invoice, payment, onPaymentUpdated }) => {
  const [paymentData, setPaymentData] = useState({
    date: new Date(),
    paid_by: '',
    amount: '',
    payment_mode: '',
    transaction_id: '',
    receipt_number: '',
    comments: ''
  });
  const [loading, setLoading] = useState(false);
  const [projectUsers, setProjectUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [allocationMode, setAllocationMode] = useState('proportional');
  const [customAllocations, setCustomAllocations] = useState([]);

  // Populate form with existing payment data
  useEffect(() => {
    if (!open || !payment) return;

    const paidById = typeof payment.paid_by === 'object'
      ? payment.paid_by?._id
      : payment.paid_by;

    setPaymentData({
      date: payment.date ? new Date(payment.date) : new Date(),
      paid_by: paidById || '',
      amount: payment.amount?.toString() || '',
      payment_mode: payment.payment_mode || '',
      transaction_id: payment.transaction_id || '',
      receipt_number: payment.receipt_number || '',
      comments: payment.comments || ''
    });
  }, [open, payment]);

  // Fetch project users when dialog opens
  useEffect(() => {
    const fetchProjectUsers = async () => {
      if (!open || !invoice) return;

      setLoadingUsers(true);
      try {
        const projectId = invoice.project?._id ||
                          invoice.projectInfo?._id ||
                          (typeof invoice.project === 'string' ? invoice.project : null);

        if (!projectId) {
          setLoadingUsers(false);
          return;
        }

        const response = await axios.get(`/api/projects/${projectId}`);
        const project = response.data;

        if (project?.users && Array.isArray(project.users)) {
          const users = project.users.map(user => {
            let userId = null;
            if (typeof user.user === 'object' && user.user?._id) {
              userId = user.user._id;
            } else if (typeof user.user === 'string') {
              userId = user.user;
            } else if (user._id) {
              userId = user._id;
            }

            let role = 'Member';
            if (user.role) {
              if (typeof user.role === 'object') {
                role = user.role.role || user.role.name || 'Member';
              } else {
                role = user.role;
              }
            }

            return {
              _id: userId,
              first_name: user.first_name || 'Unknown',
              last_name: user.last_name || 'User',
              email: user.email || '',
              role: role
            };
          }).filter(user => user._id);

          setProjectUsers(users);

          // If paid_by user is not in the project users list (e.g. external), add them
          const paidById = typeof payment?.paid_by === 'object'
            ? payment.paid_by?._id
            : payment?.paid_by;
          if (paidById && !users.find(u => u._id === paidById) && typeof payment?.paid_by === 'object') {
            setProjectUsers(prev => [...prev, {
              _id: paidById,
              first_name: payment.paid_by.first_name || 'Unknown',
              last_name: payment.paid_by.last_name || 'User',
              email: payment.paid_by.email || '',
              role: 'External'
            }]);
          }
        }
      } catch (error) {
        console.error('Error fetching project users:', error);
        if (invoice.project?.users && Array.isArray(invoice.project.users)) {
          const users = invoice.project.users.map(user => ({
            _id: user.user?._id || user.user || user._id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            role: typeof user.role === 'object' ? (user.role.role || 'Member') : (user.role || 'Member')
          })).filter(u => u._id);
          setProjectUsers(users);
        }
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchProjectUsers();
  }, [open, invoice, payment]);

  const handleFetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await axios.get('/api/users/all');
      if (response.data && Array.isArray(response.data)) {
        const users = response.data.map(user => ({
          _id: user._id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          role: user.institution_roles?.map(r => r.role || r).join(', ') || 'Not Disclosed'
        }));
        setProjectUsers(users);
        toast.success(`Loaded ${users.length} users`);
      } else {
        toast.error('No users found in system');
      }
    } catch (error) {
      console.error('Error fetching all users:', error);
      toast.error('Could not load users: ' + error.message);
    } finally {
      setLoadingUsers(false);
    }
  };

  const distType = invoice?.distribution_type;
  const distEntries = invoice?.amount_distribution || [];
  const hasSplitDistribution = distEntries.length > 1;
  const editPaymentAmount = parseFloat(paymentData.amount) || 0;
  const showAllocationSection = hasSplitDistribution && editPaymentAmount > 0;
  const isAccountBased = distType === 'account_based';
  const splitLabel = isAccountBased ? 'accounts' : 'users';

  const distributionItems = useMemo(() => {
    if (!distEntries || distEntries.length === 0) return [];
    return distEntries
      .filter(d => d.identifier)
      .map(d => ({
        identifier: d.identifier,
        user_id: d.user_id?._id || d.user_id,
        display_name: d.display_name || d.identifier,
        percent_share: d.percent_share || 0,
        amount_share: d.amount_share || 0
      }));
  }, [distEntries]);

  useEffect(() => {
    if (!showAllocationSection || distributionItems.length === 0) {
      setCustomAllocations([]);
      return;
    }
    const totalInvoiceAmount = invoice?.total_amount || 1;
    setCustomAllocations(
      distributionItems.map(item => {
        const share = item.amount_share / totalInvoiceAmount;
        return {
          identifier: item.identifier,
          account_number: item.identifier,
          user_id: item.user_id,
          display_name: item.display_name,
          percent_share: item.percent_share,
          amount: parseFloat((editPaymentAmount * share).toFixed(2))
        };
      })
    );
  }, [editPaymentAmount, showAllocationSection, distributionItems, invoice?.total_amount]);

  const allocationTotal = customAllocations.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
  const allocationDiff = Math.abs(allocationTotal - editPaymentAmount);
  const allocationValid = allocationDiff <= 0.01;

  const handleAllocationChange = (index, value) => {
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setCustomAllocations(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], amount: value === '' ? '' : value };
        return updated;
      });
    }
  };

  const handleDistributeEqually = () => {
    const count = distributionItems.length;
    const perItem = parseFloat((editPaymentAmount / count).toFixed(2));
    const remainder = parseFloat((editPaymentAmount - perItem * count).toFixed(2));
    setCustomAllocations(prev =>
      prev.map((a, i) => ({
        ...a,
        amount: i === 0 ? parseFloat((perItem + remainder).toFixed(2)) : perItem
      }))
    );
  };

  const handleDistributeProportionally = () => {
    const totalInvoiceAmount = invoice?.total_amount || 1;
    const updated = distributionItems.map((item, i) => ({
      ...(customAllocations[i] || {}),
      identifier: item.identifier,
      account_number: item.identifier,
      user_id: item.user_id,
      display_name: item.display_name,
      percent_share: item.percent_share,
      amount: parseFloat((editPaymentAmount * (item.amount_share / totalInvoiceAmount)).toFixed(2))
    }));
    const total = updated.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
    const diff = parseFloat((editPaymentAmount - total).toFixed(2));
    if (Math.abs(diff) > 0 && updated.length > 0) {
      updated[0].amount = parseFloat(((parseFloat(updated[0].amount) || 0) + diff).toFixed(2));
    }
    setCustomAllocations(updated);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'amount') {
      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
        setPaymentData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setPaymentData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!paymentData.amount) {
      toast.error('Amount is required');
      return;
    }
    if (!paymentData.payment_mode) {
      toast.error('Payment mode is required');
      return;
    }
    if (!paymentData.paid_by) {
      toast.error('Paid by user is required');
      return;
    }

    if (showAllocationSection && allocationMode === 'custom' && !allocationValid) {
      toast.error(`Allocation amounts must sum to the payment amount ($${editPaymentAmount.toFixed(2)}). Current total: $${allocationTotal.toFixed(2)}`);
      return;
    }

    setLoading(true);
    try {
      const paymentRequest = {
        date: paymentData.date,
        amount: parseFloat(paymentData.amount),
        payment_mode: paymentData.payment_mode,
        transaction_id: paymentData.transaction_id,
        receipt_number: paymentData.receipt_number,
        comments: paymentData.comments,
        paid_by: paymentData.paid_by
      };

      if (showAllocationSection && customAllocations.length > 0) {
        if (allocationMode === 'custom') {
          paymentRequest.payment_allocation = customAllocations
            .filter(a => parseFloat(a.amount) > 0)
            .map(a => ({
              user_id: a.user_id,
              account_number: a.identifier,
              identifier: a.identifier,
              display_name: a.display_name,
              amount: parseFloat(a.amount)
            }));
        } else {
          const totalInv = invoice?.total_amount || 1;
          paymentRequest.payment_allocation = distributionItems
            .filter(d => d.amount_share > 0)
            .map(d => ({
              user_id: d.user_id,
              account_number: d.identifier,
              identifier: d.identifier,
              display_name: d.display_name,
              amount: parseFloat((editPaymentAmount * (d.amount_share / totalInv)).toFixed(2))
            }));
        }
      }

      const response = await axios.put(
        `/api/invoices/${invoice._id || invoice.parentId}/payment/${payment._id}?invoice_id=${invoice.invoice_id}`,
        paymentRequest
      );

      toast.success('Payment updated successfully');

      if (onPaymentUpdated) {
        onPaymentUpdated(response.data);
      }

      onClose();
    } catch (error) {
      console.error('Error updating payment:', error);
      toast.error('Failed to update payment: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const paidByName = typeof payment?.paid_by === 'object'
    ? getUserDisplayName(payment.paid_by)
    : 'Unknown';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="edit-payment-title"
    >
      <DialogTitle id="edit-payment-title">
        Edit Payment for Invoice #{getInvoiceNumber(invoice?.invoice_id)}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          Editing payment originally recorded as <strong>${payment?.amount?.toFixed(2)}</strong> by <strong>{paidByName}</strong>.
        </Alert>

        {!loadingUsers && projectUsers.length === 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleFetchAllUsers}>
                Fetch All Users
              </Button>
            }
          >
            No project users found. Click "Fetch All Users" to load all system users.
          </Alert>
        )}

        <Box component="form">
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Payment Date"
                value={paymentData.date instanceof Date
                  ? paymentData.date.toISOString().split('T')[0]
                  : new Date(paymentData.date).toISOString().split('T')[0]}
                onChange={(e) => setPaymentData(prev => ({
                  ...prev,
                  date: e.target.value ? new Date(e.target.value) : prev.date
                }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Paid By</InputLabel>
                <Select
                  name="paid_by"
                  value={paymentData.paid_by}
                  onChange={handleChange}
                  label="Paid By"
                  disabled={loadingUsers}
                >
                  {loadingUsers ? (
                    <MenuItem disabled>Loading users...</MenuItem>
                  ) : projectUsers.length > 0 ? (
                    projectUsers.map(user => (
                      <MenuItem key={user._id} value={user._id}>
                        {user.first_name} {user.last_name} ({user.role})
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No users available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="amount"
                label="Amount"
                value={paymentData.amount}
                onChange={handleChange}
                fullWidth
                required
                type="text"
                inputProps={{
                  inputMode: 'decimal',
                  pattern: '^[0-9]*.?[0-9]{0,2}$'
                }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Payment Mode</InputLabel>
                <Select
                  name="payment_mode"
                  value={paymentData.payment_mode}
                  onChange={handleChange}
                  label="Payment Mode"
                >
                  <MenuItem value="Credit Card">Credit Card</MenuItem>
                  <MenuItem value="CBO">CBO (CALS Business Office)</MenuItem>
                  <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  <MenuItem value="Check">Check</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="transaction_id"
                label="Transaction ID (optional)"
                value={paymentData.transaction_id}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="receipt_number"
                label="Receipt Number (optional)"
                value={paymentData.receipt_number}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="comments"
                label="Comments (optional)"
                value={paymentData.comments}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            {/* Payment Allocation for Split-Bill Invoices */}
            {showAllocationSection && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Payment Allocation Across {isAccountBased ? 'Accounts' : 'Users'}
                  </Typography>
                  <Chip label="Split Invoice" color="info" size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This invoice is split across {distributionItems.length} {splitLabel}. Choose how to allocate this payment.
                </Typography>

                <ToggleButtonGroup
                  value={allocationMode}
                  exclusive
                  onChange={(e, val) => { if (val) setAllocationMode(val); }}
                  size="small"
                  sx={{
                    mb: 2,
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      px: 2,
                      color: 'text.secondary',
                      borderColor: 'divider',
                      '&.Mui-selected': {
                        backgroundColor: 'var(--brand-primary-dark, #8b0000)',
                        color: '#fff',
                        fontWeight: 600,
                        borderColor: 'var(--brand-primary-dark, #8b0000)',
                        '&:hover': { backgroundColor: 'var(--brand-primary-active, #6d0000)' }
                      }
                    }
                  }}
                >
                  <ToggleButton value="proportional">Proportional (Auto)</ToggleButton>
                  <ToggleButton value="custom">Custom Allocation</ToggleButton>
                </ToggleButtonGroup>

                {allocationMode === 'proportional' && (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {isAccountBased ? (
                            <>
                              <TableCell>Account</TableCell>
                              <TableCell>Owner</TableCell>
                            </>
                          ) : (
                            <TableCell>User</TableCell>
                          )}
                          <TableCell align="right">Share %</TableCell>
                          <TableCell align="right">Invoice Share</TableCell>
                          <TableCell align="right">Payment Share</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {distributionItems.map((item) => {
                          const totalInv = invoice?.total_amount || 1;
                          const pShare = item.amount_share / totalInv;
                          return (
                            <TableRow key={`${item.user_id}-${item.identifier}`}>
                              {isAccountBased ? (
                                <>
                                  <TableCell sx={{ fontFamily: 'monospace' }}>{item.identifier}</TableCell>
                                  <TableCell>{item.display_name}</TableCell>
                                </>
                              ) : (
                                <TableCell>{item.display_name}</TableCell>
                              )}
                              <TableCell align="right">{item.percent_share?.toFixed(1)}%</TableCell>
                              <TableCell align="right">${item.amount_share?.toFixed(2)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                ${(editPaymentAmount * pShare).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {allocationMode === 'custom' && (
                  <>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Button size="small" variant="outlined" onClick={handleDistributeEqually}>
                        Split Equally
                      </Button>
                      <Button size="small" variant="outlined" onClick={handleDistributeProportionally}>
                        Reset to Proportional
                      </Button>
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {isAccountBased ? (
                              <>
                                <TableCell>Account</TableCell>
                                <TableCell>Owner</TableCell>
                              </>
                            ) : (
                              <TableCell>User</TableCell>
                            )}
                            <TableCell align="right">Invoice Share</TableCell>
                            <TableCell align="right" sx={{ width: 160 }}>Payment Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {customAllocations.map((alloc, idx) => (
                            <TableRow key={`${alloc.user_id}-${alloc.identifier}`}>
                              {isAccountBased ? (
                                <>
                                  <TableCell sx={{ fontFamily: 'monospace' }}>{alloc.identifier}</TableCell>
                                  <TableCell>{alloc.display_name}</TableCell>
                                </>
                              ) : (
                                <TableCell>{alloc.display_name}</TableCell>
                              )}
                              <TableCell align="right">
                                ${distributionItems[idx]?.amount_share?.toFixed(2) || '0.00'}
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  size="small"
                                  value={alloc.amount}
                                  onChange={(e) => handleAllocationChange(idx, e.target.value)}
                                  type="text"
                                  inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                                  InputProps={{
                                    startAdornment: <Typography sx={{ mr: 0.5, fontSize: '0.875rem' }}>$</Typography>
                                  }}
                                  sx={{ width: 130 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell colSpan={isAccountBased ? 3 : 2} align="right" sx={{ fontWeight: 600 }}>
                              Total Allocated:
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              <Typography
                                component="span"
                                sx={{ color: allocationValid ? 'success.main' : 'error.main', fontWeight: 600 }}
                              >
                                ${allocationTotal.toFixed(2)}
                              </Typography>
                              <Typography component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                                / ${editPaymentAmount.toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {!allocationValid && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        Allocation total (${allocationTotal.toFixed(2)}) does not match payment amount (${editPaymentAmount.toFixed(2)}).
                        Difference: ${allocationDiff.toFixed(2)}
                      </Alert>
                    )}
                  </>
                )}
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || loadingUsers}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPayment;
